import { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { useAdmin } from "./AdminLayout";
import { Upload } from "lucide-react";

export default function AdminDonations() {
  const user = useAdmin();
  const canImport = user.permissions.includes("donations.import");
  const [donations, setDonations] = useState([]);
  const [summary, setSummary] = useState([]);
  const [campaign, setCampaign] = useState("");
  const [state, setState] = useState({ status: "idle", message: "" });

  const load = () => {
    api.get(`/admin/donations${campaign ? `?campaign=${encodeURIComponent(campaign)}` : ""}`)
      .then(({ data }) => setDonations(data.donations)).catch(() => {});
    api.get("/admin/donations/summary").then(({ data }) => setSummary(data.campaigns)).catch(() => {});
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, []);

  const importCsv = async (e) => {
    e.preventDefault();
    const file = e.target.elements.csv.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setState({ status: "loading", message: "" });
    try {
      const { data } = await api.post("/admin/donations/import", fd);
      setState({ status: "success", message: `Imported ${data.imported} record(s); ${data.skipped} skipped (duplicates or invalid rows).` });
      e.target.reset();
      load();
    } catch (err) {
      setState({ status: "error", message: formatApiError(err) });
    }
  };

  return (
    <div data-testid="admin-donations">
      <h1 className="font-serif text-3xl font-semibold text-forest">Donation reporting</h1>
      <p className="mt-2 text-sm text-charcoal/70 max-w-2xl leading-relaxed">
        Read-only records imported from official donation platform reports (e.g. JustGiving).
        Figures here never include unverified data, and anonymous donors' identities remain protected.
      </p>

      <div className="mt-8 grid gap-5 sm:grid-cols-3">
        {summary.map((c) => (
          <div key={c.campaign} className="bg-white border border-sandstone p-6">
            <p className="text-xs tracking-wider uppercase font-semibold text-charcoal/60">{c.campaign}</p>
            <p className="mt-2 font-serif text-3xl font-semibold text-forest">£{c.total.toLocaleString("en-GB")}</p>
            <p className="mt-1 text-xs text-charcoal/60">{c.count} donation(s)</p>
          </div>
        ))}
        {summary.length === 0 && (
          <div className="bg-sandstone/50 border border-sandstone p-6 sm:col-span-3 text-sm text-charcoal/70" data-testid="donations-empty">
            No donation records have been imported yet.
          </div>
        )}
      </div>

      {canImport && (
        <form onSubmit={importCsv} className="mt-8 bg-white border border-sandstone p-6" data-testid="donation-import-form">
          <h2 className="font-serif text-xl font-semibold text-forest flex items-center gap-2">
            <Upload size={18} aria-hidden="true" /> Import donation records (CSV)
          </h2>
          <p className="mt-2 text-xs text-charcoal/60 leading-relaxed">
            Super Administrator only. Expected columns: donor_name, amount, date; optional:
            currency, campaign, status, anonymous, reference. Use reports exported from your
            official JustGiving account. Files are validated, sanitised and
            de-duplicated; every import is recorded in the audit log.
          </p>
          <div className="mt-4 flex flex-col sm:flex-row gap-3">
            <input name="csv" type="file" accept=".csv" required data-testid="donation-import-file"
              className="text-sm text-charcoal file:bg-forest file:text-white file:border-0 file:px-4 file:py-2 file:mr-3" />
            <button type="submit" disabled={state.status === "loading"} data-testid="donation-import-submit"
              className="bg-forest text-white text-sm font-medium px-6 py-2.5 hover:bg-forest-dark transition-colors duration-300 disabled:opacity-60">
              {state.status === "loading" ? "Importing…" : "Import"}
            </button>
          </div>
          {state.status === "success" && <p className="mt-3 text-sm text-forest" data-testid="import-success">{state.message}</p>}
          {state.status === "error" && <p className="mt-3 text-sm text-red-700" role="alert" data-testid="import-error">{state.message}</p>}
        </form>
      )}

      <div className="mt-8 flex items-center gap-3">
        <label htmlFor="campaign-filter" className="text-xs tracking-wider uppercase font-semibold text-charcoal/60">Campaign</label>
        <input id="campaign-filter" value={campaign} onChange={(e) => setCampaign(e.target.value)}
          placeholder="Filter…" data-testid="donation-campaign-filter"
          className="border border-sage bg-white px-3 py-2 text-sm rounded-sm" />
        <button onClick={load} data-testid="donation-filter-apply"
          className="text-sm bg-forest text-white px-4 py-2 hover:bg-forest-dark transition-colors duration-300">Apply</button>
      </div>

      <div className="mt-4 bg-white border border-sandstone overflow-x-auto">
        <table className="w-full text-sm" data-testid="donations-table">
          <thead>
            <tr className="text-left border-b border-sandstone text-xs uppercase tracking-wider text-charcoal/60">
              <th className="px-5 py-3 font-semibold">Donor</th>
              <th className="px-5 py-3 font-semibold">Amount</th>
              <th className="px-5 py-3 font-semibold">Date</th>
              <th className="px-5 py-3 font-semibold">Campaign</th>
              <th className="px-5 py-3 font-semibold">Status</th>
              <th className="px-5 py-3 font-semibold">Source</th>
            </tr>
          </thead>
          <tbody>
            {donations.map((d) => (
              <tr key={d.id} className="border-b border-sandstone/60 last:border-0">
                <td className="px-5 py-3">{d.donor_name}{d.anonymous && <span className="ml-2 text-xs text-charcoal/50">(anonymous)</span>}</td>
                <td className="px-5 py-3">{d.currency} {Number(d.amount).toLocaleString("en-GB", { minimumFractionDigits: 2 })}</td>
                <td className="px-5 py-3">{d.date}</td>
                <td className="px-5 py-3">{d.campaign || "—"}</td>
                <td className="px-5 py-3">{d.status}</td>
                <td className="px-5 py-3 text-xs text-charcoal/60">CSV import</td>
              </tr>
            ))}
            {donations.length === 0 && (
              <tr><td colSpan={6} className="px-5 py-8 text-center text-charcoal/50">No records to display.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
